// Threads between a buyer and a seller about one lot.
//
// The unread badge is kept here so the account area and the lot page agree,
// and it is refreshed both by the websocket ("message:new") and by a slow
// poll, so a missed socket frame costs a minute, not the whole conversation.
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { messageService, type Conversation, type ChatMessage } from '@/services/messageService'
import { useUserStore } from '@/stores/user'

const POLL_MS = 60_000

export const useMessagesStore = defineStore('messages', () => {
  const conversations = ref<Conversation[]>([])
  const messages = ref<ChatMessage[]>([])
  const activeId = ref<string | null>(null)
  const unread = ref(0)
  const isLoadingList = ref(false)
  const isLoadingThread = ref(false)
  const isSending = ref(false)
  let pollTimer: ReturnType<typeof setInterval> | null = null

  const activeConversation = computed(() => conversations.value.find(c => c.id === activeId.value) || null)

  async function fetchConversations() {
    if (!useUserStore().isAuthenticated) return
    isLoadingList.value = true
    try {
      const res = await messageService.listConversations()
      conversations.value = res.data
      unread.value = res.unread
    } catch {
      // A failed refresh keeps the last known list rather than blanking it.
    } finally {
      isLoadingList.value = false
    }
  }

  async function refreshUnread() {
    if (!useUserStore().isAuthenticated) { unread.value = 0; return }
    try {
      unread.value = await messageService.unreadCount()
    } catch { /* keep the previous number */ }
  }

  async function openThread(id: string) {
    activeId.value = id
    isLoadingThread.value = true
    messages.value = []
    try {
      const res = await messageService.getThread(id)
      // Another thread was opened while this one loaded: leave it alone.
      if (activeId.value !== id) return
      messages.value = res.messages
      const idx = conversations.value.findIndex(c => c.id === id)
      if (idx >= 0) conversations.value[idx] = { ...res.conversation, unread: 0 }
      else conversations.value = [{ ...res.conversation, unread: 0 }, ...conversations.value]
      await refreshUnread()
    } finally {
      if (activeId.value === id) isLoadingThread.value = false
    }
  }

  function closeThread() {
    activeId.value = null
    messages.value = []
    isLoadingThread.value = false
  }

  /** Opens (or reuses) the thread for a lot and optionally sends the first message. */
  async function contactSeller(auctionId: string, body?: string): Promise<string> {
    const { conversationId } = await messageService.openForAuction(auctionId, body)
    await fetchConversations()
    return conversationId
  }

  async function send(body: string, photos: File[] = []) {
    // The thread is fixed when Send is pressed: an upload can take a while,
    // and the user may open another thread before it finishes.
    const threadId = activeId.value
    if (!threadId || isSending.value) return
    isSending.value = true
    try {
      const message = photos.length
        ? await messageService.sendPhotos(threadId, photos, body)
        : await messageService.send(threadId, body)
      if (activeId.value === threadId) messages.value = [...messages.value, message]
      const idx = conversations.value.findIndex(c => c.id === threadId)
      if (idx >= 0) {
        conversations.value[idx] = {
          ...conversations.value[idx],
          lastMessage: message.body.slice(0, 160),
          lastPhotos: message.photos?.length || 0,
          lastMessageAt: message.createdAt,
          lastFromMe: true,
        }
      }
    } finally {
      isSending.value = false
    }
  }

  /** A "message:new" frame for the open thread appends it; otherwise it only moves the badge. */
  async function onIncoming(conversationId?: string) {
    if (conversationId && conversationId === activeId.value) {
      try {
        const res = await messageService.getThread(conversationId)
        messages.value = res.messages
      } catch { /* the poll will catch up */ }
    }
    await Promise.all([refreshUnread(), fetchConversations()])
  }

  function startPolling() {
    if (pollTimer) return
    refreshUnread()
    pollTimer = setInterval(refreshUnread, POLL_MS)
  }

  function stopPolling() {
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = null
  }

  function reset() {
    conversations.value = []
    messages.value = []
    activeId.value = null
    isLoadingThread.value = false
    unread.value = 0
    stopPolling()
  }

  return {
    conversations,
    messages,
    activeId,
    activeConversation,
    unread,
    isLoadingList,
    isLoadingThread,
    isSending,
    fetchConversations,
    refreshUnread,
    openThread,
    closeThread,
    contactSeller,
    send,
    onIncoming,
    startPolling,
    stopPolling,
    reset,
  }
})
